import React, { useState } from "react";
import { adminApi } from "../api";

/**
 * Master live/sandbox toggle for a venue. Lives at the top of the
 * AdminVenueDetail page. When set to "sandbox", the public signup
 * form, the public Venue API v1, and any signup-flow path resolve to
 * the venue's sandbox credential set instead of live.
 *
 * The toggle deliberately does NOT touch what credentials Integration
 * Tests use — those force sandbox regardless of this value.
 */
export default function VenueModeToggle({ venue, onChange }) {
  const [mode, setMode] = useState(venue?.signup_mode || "live");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function set(next) {
    if (next === mode) return;
    if (next === "sandbox" && !window.confirm(
      "Switch this venue to SANDBOX mode? The public signup form will start using the sandbox credential set — no real Stripe charges, no live provider records, no live emails. Switch back to Live when you're done."
    )) return;
    if (next === "live" && !window.confirm("Switch this venue back to LIVE mode? Real charges and live provider records will start again.")) return;

    setBusy(true); setError("");
    try {
      await adminApi.updateVenue(venue.id, { signup_mode: next });
      setMode(next);
      if (onChange) onChange(next);
    } catch (err) { setError(err.message); }
    setBusy(false);
  }

  return (
    <div className={`venue-mode-card mode-${mode}`}>
      <div className="venue-mode-row">
        <div className="venue-mode-label">
          <span className="venue-mode-dot" />
          <strong>Mode:</strong>
          <span className="venue-mode-current">{mode === "sandbox" ? "Sandbox" : "Live"}</span>
        </div>
        <div className="venue-mode-toggle" role="radiogroup">
          <button
            type="button"
            className={`venue-mode-btn ${mode === "live" ? "is-active" : ""}`}
            onClick={() => set("live")}
            disabled={busy}
          >Live</button>
          <button
            type="button"
            className={`venue-mode-btn ${mode === "sandbox" ? "is-active" : ""}`}
            onClick={() => set("sandbox")}
            disabled={busy}
          >Sandbox</button>
        </div>
      </div>
      {mode === "sandbox" && (
        <div className="venue-mode-warning">
          ⚠ TEST MODE — public signup form and Venue API v1 are using sandbox
          credentials. No real charges or live provider records will be created.
        </div>
      )}
      {error && <div className="venue-mode-error">{error}</div>}

      <style>{`
        .venue-mode-card {
          padding: 0.85rem 1.1rem;
          border: 1px solid var(--border);
          border-radius: 8px;
          margin-bottom: 1rem;
          background: var(--bg-subtle);
          transition: background 0.15s, border-color 0.15s;
        }
        .venue-mode-card.mode-sandbox {
          background: color-mix(in srgb, var(--warning, #d97706) 10%, var(--bg-subtle));
          border-color: var(--warning, #d97706);
        }
        .venue-mode-row {
          display: flex; align-items: center; justify-content: space-between;
          gap: 1rem; flex-wrap: wrap;
        }
        .venue-mode-label {
          display: inline-flex; align-items: center; gap: 0.5rem;
          font-size: 0.9rem; color: var(--text);
        }
        .venue-mode-dot {
          width: 10px; height: 10px; border-radius: 50%;
          background: var(--success, #16a34a);
        }
        .mode-sandbox .venue-mode-dot { background: var(--warning, #d97706); }
        .venue-mode-current {
          padding: 0.1rem 0.55rem;
          background: var(--bg-elevated);
          border-radius: 999px;
          font-size: 0.78rem;
          font-weight: 500;
        }
        .venue-mode-toggle {
          display: inline-flex; border: 1px solid var(--border);
          border-radius: 6px; overflow: hidden;
        }
        .venue-mode-btn {
          background: var(--bg-elevated);
          border: 0;
          padding: 0.4rem 1rem;
          font-size: 0.82rem;
          color: var(--text-muted);
          cursor: pointer;
          transition: background 0.15s, color 0.15s;
        }
        .venue-mode-btn.is-active {
          background: var(--accent);
          color: white;
        }
        .venue-mode-btn:not(.is-active):hover { background: var(--bg-surface); color: var(--text); }
        .venue-mode-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .venue-mode-warning {
          margin-top: 0.7rem;
          padding: 0.55rem 0.75rem;
          background: var(--warning-soft, rgba(245,158,11,0.12));
          border: 1px solid color-mix(in srgb, var(--warning, #d97706) 35%, transparent);
          border-radius: 6px;
          font-size: 0.82rem;
          color: var(--text);
        }
        .venue-mode-error {
          margin-top: 0.5rem;
          color: var(--danger, #dc2626);
          font-size: 0.85rem;
        }
      `}</style>
    </div>
  );
}
